import { getFilteredCustomers } from 'client/actions/customer'
import { setLoading } from 'client/actions/expenseActions'
import { getCustomerForSearchedText } from 'client/api/customer'
import { Analytics } from 'client/Global/Analytics'
import { PremiumModalOrigin, trackingFields } from 'client/Global/AnalyticsEvents'
import { useDebounce } from 'client/hooks/useDebounced'
import { QuotaTypes, SubscriptionInterface } from 'client/Interfaces/Subscription'
import { l } from 'client/Other/Translation'
import { useTypedSelector } from 'client/reducer'
import { subscriptionCompanySubscriptionSelector } from 'client/selectors/subscription'
import * as React from 'react'
import { useDispatch } from 'react-redux'

import { navigateAddModal } from '../../actions/navigation'
import { CustomerCollectionInterface, CustomerInterface } from '../../Interfaces/CustomerInterface'
import { getCustomerAddingAction, getQuotaInfo } from '../../reducers/Subscription/companySubscription'
import { AutoCompleteMaxAllowedInterface } from '../common/CustomAutoComplete/CustomAutoComplete'
import DropSelect from '../common/DropSelect'
import { PremiumFeatureType } from '../Modal/PremiumFeatureIntermediaryModal'
import './CustomerSelect.scss'

interface IActionProps {
  actions: {
    openSubscriptionModal: (next) => () => void
    showIntermediaryModal: (next) => void
  }
}

export interface IExternalProps {
  renderItem?: (item, isHighlighted) => any
  maxAllowed?: AutoCompleteMaxAllowedInterface
  customers: CustomerInterface[]
  selected?: CustomerInterface
  highlight?: boolean
  onClickOutside?: () => void
  onOpen?: () => void
  onClose?: () => void
  onChange?: (customer: CustomerInterface) => void
  invalid?: boolean
  preserveOrder?: boolean
  openCustomerModal?: (customerName: string) => void
  openCustomerSelect?: boolean
  customerAddingAction?: any
}

export type ICustomerSelectProps = IExternalProps & IActionProps
export const CustomerSelectClass: React.FC<ICustomerSelectProps> = (props): JSX.Element => {
  const dispatch = useDispatch()
  const state = useTypedSelector((state) => state)
  const [touched, setTouched] = React.useState(false)
  const [searchButtonForOlderCustomers, setSearchButtonForOlderCustomers] = React.useState(false)
  const [searchField, setSearchField] = React.useState('')
  const [loadingCustomers, setLoadingCustomers] = React.useState(false)
  const [loadedCustomers, setLoadedCustomers] = React.useState([])
  const {
    customers,
    invalid,
    maxAllowed,
    openCustomerSelect,
    highlight,
    selected,
    openCustomerModal,
    onChange,
    onClose,
    onOpen,
    onClickOutside,
  } = props
  const customerAddingAction = getCustomerAddingAction(state)
  const isQuotaAvailable = getQuotaInfo(state, QuotaTypes.CUSTOMERS)?.isQuotaAvailable
  const subscription = subscriptionCompanySubscriptionSelector(state)
  const deboundSearchText = useDebounce(searchField, 2000)
  const deboundLoadding = useDebounce(searchField, 500)

  React.useEffect(() => {
    limitActionClick(deboundSearchText)
  }, [deboundSearchText])

  React.useEffect(() => {
    setLoadingCustomers(true)
  }, [deboundLoadding])

  const openSubscriptionModal = (next) => () =>
    dispatch(
      navigateAddModal({
        component: 'PremiumPlanModal',
        props: {
          origin: PremiumModalOrigin.Customers,
          hideDowngradePlans: true,
        },
      }),
    )

  const showIntermediaryModal = (next: () => void) =>
    dispatch(
      navigateAddModal({
        component: 'PremiumFeatureIntermediaryModal',
        props: {
          isOpen: true,
          featureType: PremiumFeatureType.CUSTOMERS,
          next,
        },
      }),
    )

  const onSearchFieldChange = (searchText: string) => {
    setSearchField(searchText)
  }

  const onAddNew = (searchText) => {
    if (customerAddingAction === 'canAdd') {
      openCustomerModal(searchText)
    } else if (customerAddingAction === 'canUpgrade') {
      openSubscriptionModal(() => openCustomerModal(searchText))()
    } else {
      showIntermediaryModal(openSubscriptionModal(openCustomerModal))
    }
  }

  const searchOlderCustomersForMatchingSearchedText = (searchText: string) => {
    let customers = [] as CustomerInterface[]
    let terms = String(searchText)
      ?.split(/[ ]+/)
      .filter((t) => t)
      .map((t) => t.toLowerCase())
    terms = terms.length > 0 ? terms : ['']
    Promise.all(terms?.map((term) => getCustomerForSearchedText(term)))?.then((result) => {
      customers = getFilteredCustomers(result?.flat(), searchText)
      setLoadedCustomers(customers)
      setLoadingCustomers(false)
    })
    setSearchButtonForOlderCustomers(true)
  }

  const getCustomersList = () => {
    if (!searchButtonForOlderCustomers) {
      return [...(props.customers || [])]
    } else if (searchButtonForOlderCustomers) {
      return loadedCustomers
    }
  }
  const limitActionClick = (searchText) => {
    searchOlderCustomersForMatchingSearchedText(searchText)
    Analytics.trackEvent(trackingFields.name.searchMoreCustomers, {
      type: trackingFields.type.action,
      category: trackingFields.category.customer,
      result: trackingFields.result.success,
      placement: trackingFields.placement.editor,
    })
  }

  const getCustomerName = (item) => {
    if (item) {
      return item?.companyName || `${item?.firstName} ${item?.lastName}` || item?.name
    }
  }

  const renderItem = (item: CustomerInterface, isHighlighted) => {
    const classes = []
    if (item?.unavailable) {
      classes.push('DropdownItemUnavailable')
    } else if (isHighlighted) {
      classes.push('highLightedItem')
    }
    return (
      <div className={classes.join(' ')} key={item?.id || item?.customerId}>
        {getCustomerName(item)}
      </div>
    )
  }

  if (customers) {
    customers.forEach((c) => {
      c.name = c?.displayName || c?.companyName
    })
  }
  const classes = ['CustomerSelect']
  if (invalid) {
    classes.push('invalid')
  }

  return (
    <div className={classes.join(' ')}>
      <DropSelect<CustomerInterface>
        isDataLoading={loadingCustomers}
        automation={'editorCustomerSelector'}
        className={'editorCustomerSelector'}
        open={openCustomerSelect}
        maxAllowed={maxAllowed || { limit: -1 }}
        highlight={highlight}
        onClickOutside={onClickOutside}
        hasError={invalid}
        noItemsFoundText={l('editor.customerNoOptionsFound')}
        onOpen={() => onOpen()}
        onClose={() => onClose()}
        onClick={() => setTouched(true)}
        items={getCustomersList()}
        selectedItem={selected}
        getSelectedText={(c) => getCustomerName(c)}
        placeholder={l('input.select.chooseOrAddCustomer')}
        showSearchIcon={true}
        searchField={(searchText) => onSearchFieldChange(searchText)}
        clearField={(value) => onSearchFieldChange('')}
        onSelect={(c) => onChange(c)}
        isQuotaAvailable={isQuotaAvailable}
        sortFn={(a, b) =>
          (a?.displayName || a?.companyName || a?.firstName || a?.lastName)?.localeCompare(
            b?.displayName || b?.companyName || b?.firstName || b?.lastName,
          )
        }
        renderItem={(item) => renderItem(item, false)}
        onLimitActionClick={(searchText) => searchOlderCustomersForMatchingSearchedText(searchText)}
        onActionClick={(searchText) => {
          onAddNew(searchText)
        }}
        renderAction={(item) => {
          return item?.trim() ? `${l('editor.createCustomerWithName')} '${item}'` : l('editor.createNewCustomer')
        }}
      />
      <span className="customerNotSelectedErrorMsg">{invalid && l('editor.customerNotSelectedError')}</span>
    </div>
  )
}

export const CustomerSelect = CustomerSelectClass
